// Copyright (c) 2026 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";

import { getLocalDate, getLocalDateTimeIso } from "guiutils";

// Native <input type="date"> / "datetime-local" bound to a timestamp in local time.
// The typed text is kept in local state and only valid values are reported via onChange.
// Rebuilding the input value from the timestamp on every keystroke would break typing,
// since intermediate values while typing are empty or have bogus years (e.g. "0002").
interface IDateInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange"
> {
  type: "date" | "datetime-local";
  valueMs: number;
  // for type "date": report the end instead of the start of the selected day
  endOfDay?: boolean;
  onChange: (valueMs: number) => void;
}

export default function DateInput(props: IDateInputProps) {
  // all other attributes are passed to the input element
  const { type, valueMs, endOfDay, onChange, ...inputProps } = props;
  const format = (ms: number) =>
    props.type === "date" ? getLocalDate(ms) : getLocalDateTimeIso(ms);
  const [text, setText] = React.useState(() => format(props.valueMs));
  const [syncedText, setSyncedText] = React.useState(text);

  // take over external changes of the value (not caused by typing here)
  if (format(props.valueMs) !== syncedText) {
    setSyncedText(format(props.valueMs));
    setText(format(props.valueMs));
  }

  return (
    <input
      {...inputProps}
      type={props.type}
      value={text}
      onChange={(event) => {
        const newText = event.currentTarget.value;
        setText(newText);
        // strings without offset are parsed as local time
        const newMs = new Date(
          props.type === "date" ?
            `${newText}T${props.endOfDay ? "23:59:59.999" : "00:00"}`
          : newText,
        ).getTime();
        // skip incomplete input and years still being typed
        if (isNaN(newMs) || new Date(newMs).getFullYear() < 1970) return;
        setSyncedText(format(newMs));
        props.onChange(newMs);
      }}
    />
  );
}
